import React, { useEffect } from "react";
import clsx from "clsx";
import { Avatar } from "@components/common";
import {
  Button,
  Col,
  Container,
  Nav,
  Row,
  Spinner,
  Tab,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getInstructorProfileAction,
  getTotalEnrollInstructorAction,
  getTotalReviewInstructorAction,
} from "@redux/actions";
import {
  subscribeAction,
  totalSubscriberAction,
  unsubscribeAction,
} from "@redux/modules/subscription/slices";
import { useAccessControl } from "@hooks";
import { CourseExplorer, GroupSocialLink } from "@components";
import { DEFAULT_AVATAR } from "@constants";
import styles from "./styles/InstructorProfileViewPage.module.css";

const InstructorProfileViewPage = () => {
  // state
  // variables

  // logics before hooks

  // hooks
  const { id } = useParams();
  const setSearchParams = useSearchParams()[1];
  const { isAuth } = useAccessControl();
  const navigate = useNavigate();
  const { getInstructorProfile, profile } = useSelector((state) => state.user);
  const { subscribe, unsubscribe, isSubscribed, totalSubscriber } = useSelector(
    (state) => state.subscription
  );
  const { getTotalReviewInstructor } = useSelector((state) => state.review);
  const { getTotalEnrollInstructor } = useSelector((state) => state.enrollment);
  const dispatch = useDispatch();

  // logics
  const handleSubscribe = () => {
    if (!isAuth) {
      navigate("/sign-in");
    } else {
      dispatch(subscribeAction(id));
    }
  };

  const handleUnsubscribe = () => {
    if (!isAuth) {
      navigate("/sign-in");
    } else {
      dispatch(unsubscribeAction(id));
    }
  };

  // UI partials
  const statistic = (
    <div
      className={clsx(
        styles.statistic,
        "d-flex mt-4 w-100 justify-content-between flex-wrap"
      )}
    >
      <div className="info text-center py-2 flex-1">
        <h6>Enroll Student</h6>
        {getTotalEnrollInstructor.processing ? (
          <Spinner animation="border" size="sm" variant="light" />
        ) : (
          <span>{getTotalEnrollInstructor.data ?? 0}</span>
        )}
      </div>
      <div className="info text-center py-2 flex-1">
        <h6>Courses</h6>
        {getInstructorProfile.processing ? (
          <Spinner animation="border" size="sm" variant="light" />
        ) : (
          <span>{getInstructorProfile.data?.totalCourses}</span>
        )}
      </div>
      <div className="info text-center py-2 flex-1">
        <h6>Reviews</h6>
        {getTotalReviewInstructor.processing ? (
          <Spinner animation="border" size="sm" variant="light" />
        ) : (
          <span>{getTotalReviewInstructor.data ?? 0}</span>
        )}
      </div>
      <div className="info text-center py-2 flex-1">
        <h6>Subscribers</h6>
        {totalSubscriber.processing ? (
          <Spinner animation="border" size="sm" variant="light" />
        ) : (
          <span>{totalSubscriber.data ?? 0}</span>
        )}
      </div>
    </div>
  );

  const basicInfo = (
    <div className={clsx(styles.avatar, "d-flex align-items-center gap-3")}>
      <Avatar
        className="d-inline"
        src={
          getInstructorProfile.data?.avatarUrl
            ? getInstructorProfile.data.avatarUrl
            : DEFAULT_AVATAR
        }
        width={80}
      />
      <div className="name text-white">
        <h5>{getInstructorProfile.data?.fullName}</h5>
        <div>{getInstructorProfile.data?.headLine}</div>
      </div>
    </div>
  );

  const subscribeBtn = isSubscribed.data ? (
    <Button
      variant="outline-light"
      onClick={handleUnsubscribe}
      disabled={isSubscribed.processing || unsubscribe.processing}
    >
      {(isSubscribed.processing || unsubscribe.processing) && (
        <Spinner size="sm" animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      )}{" "}
      Subscribed
    </Button>
  ) : (
    <Button
      onClick={handleSubscribe}
      disabled={isSubscribed.processing || subscribe.processing}
    >
      {(isSubscribed.processing || subscribe.processing) && (
        <Spinner size="sm" animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      )}{" "}
      Subscribe
    </Button>
  );

  const tabs = (
    <Nav variant="pills" className="pt-4 container">
      <Nav.Item className="pe-auto">
        <Nav.Link className="pe-auto" eventKey="about">
          About
        </Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link className="pe-auto" eventKey="courses">
          Courses
        </Nav.Link>
      </Nav.Item>
    </Nav>
  );

  const tabContent = (
    <Tab.Content>
      <Tab.Pane eventKey="about">
        <Container className="pt-4 px-0">
          <h4>About</h4>
          <p>{getInstructorProfile.data?.description}</p>
        </Container>
      </Tab.Pane>
      <Tab.Pane eventKey="courses">
        <Container className="px-0">
          <CourseExplorer
            title={`Courses of ${getInstructorProfile.data?.fullName || "..."}`}
            extraSearchParams={{ userId: id }}
          />
        </Container>
      </Tab.Pane>
    </Tab.Content>
  );

  // effects
  useEffect(() => {
    dispatch(getInstructorProfileAction({ userId: id }));
    dispatch(getTotalReviewInstructorAction({ userId: id }));
    dispatch(getTotalEnrollInstructorAction({ userId: id }));
    setSearchParams({ userId: id }, { replace: true });
  }, [id]);

  useEffect(() => {
    dispatch(totalSubscriberAction(id));
  }, [id, subscribe, unsubscribe]);

  // render
  return (
    <div className="">
      <div className={clsx(styles.header)}>
        <Container className="py-5">
          <Row>
            <Col lg={8} xs={12} className="leftSide mb-3">
              {basicInfo}
              {statistic}
            </Col>
            <Col
              lg={4}
              xs={12}
              className={clsx(styles.rightSide, "d-flex flex-column")}
            >
              {profile.data?.id !== id && (
                <div className="action">{subscribeBtn}</div>
              )}
              <GroupSocialLink
                facebookLink={getInstructorProfile.data?.facebookLink}
                linkedinLink={getInstructorProfile.data?.linkedlnLink}
                youtubeLink={getInstructorProfile.data?.youtubeLink}
                className={"display-inline-block"}
              />
            </Col>
          </Row>
        </Container>
      </div>

      <Container fluid>
        <Tab.Container defaultActiveKey="about">
          <Row className={clsx(styles.tabContainer)}>
            <Col sm={12} className="bg-white">
              {tabs}
            </Col>
            <Col sm={12}>{tabContent}</Col>
          </Row>
        </Tab.Container>
      </Container>
    </div>
  );
};

/* required: display name */
InstructorProfileViewPage.displayName = "InstructorProfileViewPage";

/* required: props types */
InstructorProfileViewPage.propTypes = {};

/* required: default props */
InstructorProfileViewPage.defaultProps = {};

export { InstructorProfileViewPage };
